import { z } from "zod"
import { Id } from "./ids.js"
import { Role } from "./permissions.js"

export const WorkspaceSlug = z
  .string()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "Use lowercase letters, numbers and hyphens")

export const AssignmentDefault = z.enum(["unassigned", "member"])

export const Workspace = z.object({
  id: Id,
  name: z.string().min(1).max(80),
  slug: WorkspaceSlug,
  timezone: z.string().min(1),
  iconInitials: z.string().min(1).max(2),
  assignmentDefault: AssignmentDefault,
  assignmentDefaultUserId: z.string().nullable(),
  ownerUserId: z.string(),
  createdAt: z.iso.datetime(),
})
export type Workspace = z.infer<typeof Workspace>

export const CreateWorkspaceInput = z.object({
  name: z.string().trim().min(1).max(80),
  timezone: z.string().min(1).default("UTC"),
})
export type CreateWorkspaceInput = z.infer<typeof CreateWorkspaceInput>

export const UpdateWorkspaceInput = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  timezone: z.string().min(1).optional(),
  iconInitials: z.string().trim().min(1).max(2).optional(),
  assignmentDefault: AssignmentDefault.optional(),
  assignmentDefaultUserId: z.string().nullable().optional(),
})
export type UpdateWorkspaceInput = z.infer<typeof UpdateWorkspaceInput>

export const MembershipSummary = z.object({
  workspace: Workspace,
  role: Role,
  isOwner: z.boolean(),
})
export type MembershipSummary = z.infer<typeof MembershipSummary>

export const Member = z.object({
  id: Id,
  userId: z.string(),
  name: z.string(),
  email: z.email(),
  role: Role,
  isOwner: z.boolean(),
  status: z.literal("active"),
  lastActiveAt: z.iso.datetime().nullable(),
  joinedAt: z.iso.datetime(),
})
export type Member = z.infer<typeof Member>

export const Invitation = z.object({
  id: Id,
  email: z.email(),
  role: Role,
  status: z.enum(["pending", "accepted", "cancelled", "expired"]),
  invitedByName: z.string(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
})
export type Invitation = z.infer<typeof Invitation>

export const InviteInput = z.object({ email: z.email().toLowerCase(), role: Role })
export type InviteInput = z.infer<typeof InviteInput>

/** Public view of an invitation, safe to show before sign-in. */
export const InvitationPreview = z.object({
  workspaceName: z.string(),
  workspaceInitials: z.string(),
  inviterName: z.string(),
  email: z.email(),
  role: Role,
  status: Invitation.shape.status,
})
export type InvitationPreview = z.infer<typeof InvitationPreview>

export const TeamResponse = z.object({ members: z.array(Member), invitations: z.array(Invitation) })
export type TeamResponse = z.infer<typeof TeamResponse>

export const NotificationKey = z.enum(["assigned", "mentioned", "channel_attention", "automation_intervention", "campaign_complete", "moderation"])
export type NotificationKey = z.infer<typeof NotificationKey>
export const NotificationPreferences = z.record(NotificationKey, z.boolean())
export type NotificationPreferences = z.infer<typeof NotificationPreferences>

/** A new membership starts with everything on except campaign summaries, matching the product default. */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  assigned: true,
  mentioned: true,
  channel_attention: true,
  automation_intervention: true,
  campaign_complete: false,
  moderation: true,
}

export const Me = z.object({
  id: z.string(),
  name: z.string(),
  email: z.email(),
  emailVerified: z.boolean(),
  memberships: z.array(MembershipSummary),
})
export type Me = z.infer<typeof Me>

export const UpdateMeInput = z.object({ name: z.string().trim().min(1).max(80) })
